/**
 * App Dashboard Page (Status Page)
 *
 * Main authenticated user dashboard with sidebar navigation
 * and user info display.
 *
 * @fileoverview Main /status dashboard page
 *
 * @example
 * // In router
 * <Route path="/status" element={<AppDashboard />} />
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Spinner,
  Center,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Button,
  Icon,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Input,
  HStack,
} from '@chakra-ui/react';
import { MdDelete, MdWarning } from 'react-icons/md';
import {
  UserInfoCard,
  ConnectedBanksList,
  TestConnectionLogWindow,
  ActionsCard,
  type TestConnectionResult,
  type ActionResult,
} from '../components/app';
import { useUser } from '../Context/userContext';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * Loading state component
 */
const LoadingState: React.FC = () => (
  <Center h="100vh" bg="bg.primary">
    <VStack spacing={4}>
      <Spinner size="xl" color="accent.primary" thickness="4px" />
      <Text color="text.secondary">Loading...</Text>
    </VStack>
  </Center>
);

/**
 * Danger Zone Card - Reset connection with triple confirmation
 *
 * Step 1: Click "Reset Connection" button
 * Step 2: First dialog - "Are you sure?"
 * Step 3: Second dialog - "This is irreversible"
 * Step 4: Third dialog - Type "RESET" to confirm
 */
const DangerZoneCard: React.FC = () => {
  const { selectedTenant, fetchPlaidTokenStatus } = useUser();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Triple confirmation state: 0 = closed, 1 = first, 2 = second, 3 = third (type input)
  const [confirmStep, setConfirmStep] = useState(0);
  const [confirmText, setConfirmText] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const resetConfirm = useCallback(() => {
    setConfirmStep(0);
    setConfirmText('');
  }, []);

  const handleResetConnection = useCallback(async () => {
    if (!selectedTenant) return;

    setIsResetting(true);
    resetConfirm();

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await apiFetch('/api/plaid-transactions', {
        method: 'DELETE',
        headers: {
          'x-tenant-id': selectedTenant.id,
        },
      }, session.access_token);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json().catch(() => ({ deletedTransactions: 0 }));

      toast({
        title: 'Transactions Reset',
        description: `Successfully deleted ${result.deletedTransactions?.toLocaleString() || 0} transactions.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = toUserMessage(error, 'Please try again in a moment.');
      toast({
        title: 'Reset Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResetting(false);
    }
  }, [selectedTenant, toast, resetConfirm, fetchPlaidTokenStatus]);

  return (
    <>
      <Card bg="bg.surface" borderColor="red.800" borderWidth="1px">
        <CardHeader pb={2}>
          <HStack spacing={2}>
            <Icon as={MdWarning} color="red.400" boxSize={5} />
            <Heading size="sm" color="red.400">
              Danger Zone
            </Heading>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <VStack align="stretch" spacing={3}>
            <Text color="text.secondary" fontSize="sm">
              Permanently delete all synced transactions. You will need to perform a new sync to restore your data.
            </Text>
            <Button
              leftIcon={<Icon as={MdDelete} />}
              variant="outline"
              colorScheme="red"
              borderColor="red.500"
              color="red.400"
              onClick={() => setConfirmStep(1)}
              isLoading={isResetting}
              loadingText="Resetting..."
              isDisabled={isResetting}
              w="full"
              _hover={{
                bg: 'red.900',
                borderColor: 'red.400',
              }}
            >
              Reset Transactions
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Step 1: First confirmation */}
      <AlertDialog
        isOpen={confirmStep === 1}
        leastDestructiveRef={cancelRef}
        onClose={resetConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="bg.surface" borderColor="xacto.mutedNavy" borderWidth="1px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="text.primary">
              Reset All Transactions?
            </AlertDialogHeader>
            <AlertDialogBody color="text.secondary">
              This will permanently delete all synced transactions from the database. Are you sure you want to proceed?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={resetConfirm}
                variant="ghost"
                color="text.secondary"
                _hover={{ bg: 'bg.muted' }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => setConfirmStep(2)}
                ml={3}
                bg="red.600"
                _hover={{ bg: 'red.700' }}
              >
                Yes, I'm sure
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Step 2: Second confirmation */}
      <AlertDialog
        isOpen={confirmStep === 2}
        leastDestructiveRef={cancelRef}
        onClose={resetConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="bg.surface" borderColor="red.600" borderWidth="1px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.400">
              This action is irreversible
            </AlertDialogHeader>
            <AlertDialogBody color="text.secondary">
              All transaction data will be permanently deleted. You will need to perform a full sync to restore your data. Do you really want to continue?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={resetConfirm}
                variant="ghost"
                color="text.secondary"
                _hover={{ bg: 'bg.muted' }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => setConfirmStep(3)}
                ml={3}
                bg="red.600"
                _hover={{ bg: 'red.700' }}
              >
                Permanently Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Step 3: Final confirmation - type RESET */}
      <AlertDialog
        isOpen={confirmStep === 3}
        leastDestructiveRef={cancelRef}
        onClose={resetConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="bg.surface" borderColor="red.600" borderWidth="2px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.400">
              Final Confirmation
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Text color="text.secondary">
                  Type <Text as="span" fontWeight="bold" color="red.400">RESET</Text> below to confirm the permanent deletion of all transactions.
                </Text>
                <Input
                  placeholder="Type RESET to confirm"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  bg="bg.muted"
                  borderColor="xacto.mutedNavy"
                  color="text.primary"
                  _focus={{ borderColor: 'red.500' }}
                />
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={resetConfirm}
                variant="ghost"
                color="text.secondary"
                _hover={{ bg: 'bg.muted' }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleResetConnection}
                ml={3}
                bg="red.600"
                _hover={{ bg: 'red.700' }}
                isDisabled={confirmText !== 'RESET'}
              >
                Confirm Reset
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

/**
 * Dashboard content component
 */
const DashboardContent: React.FC = () => {
  const { isLoading, error } = useUser();

  // State for test connection feature
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // State for action results
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  /**
   * Handle test result from ConnectedBanksList
   */
  const handleTestResult = useCallback((result: TestConnectionResult | null) => {
    setTestResult(result);
    setIsTesting(false);
  }, []);

  /**
   * Clear test result
   */
  const handleClearTestResult = useCallback(() => {
    setTestResult(null);
  }, []);

  /**
   * Handle action result from ActionsCard
   */
  const handleActionResult = useCallback((result: ActionResult | null) => {
    setActionResult(result);
  }, []);

  /**
   * Handle action loading state change from ActionsCard
   */
  const handleActionLoading = useCallback((loading: boolean) => {
    setIsActionLoading(loading);
  }, []);

  /**
   * Clear action result
   */
  const handleClearActionResult = useCallback(() => {
    setActionResult(null);
  }, []);

  // Show loading while user data loads
  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <VStack spacing={8} align="stretch" p={6}>
      {/* Page header */}
      <Box>
        <Heading size="lg" color="text.primary" mb={2}>
          Xacto Financial Dashboard
        </Heading>
        <Text color="text.secondary">
          Manage your bank connections and view financial data
        </Text>
      </Box>

      {/* Error display */}
      {error && (
        <Box
          bg="red.900"
          borderColor="red.500"
          borderWidth="1px"
          borderRadius="md"
          p={4}
        >
          <Text color="red.200">{error}</Text>
        </Box>
      )}

      {/* Main content - Two column layout */}
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
        gap={6}
        alignItems="start"
      >
        {/* Left column - User info, bank connection, and actions */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            <UserInfoCard />
            <ConnectedBanksList
              onTestResult={(_tokenId, result) => handleTestResult(result)}
            />
            <ActionsCard
              onActionResult={handleActionResult}
              onActionLoading={handleActionLoading}
            />
            <DangerZoneCard />
          </VStack>
        </GridItem>

        {/* Right column - Connection test log and action results */}
        <GridItem>
          <TestConnectionLogWindow
            result={testResult}
            actionResult={actionResult}
            isLoading={isTesting || isActionLoading}
            onClear={handleClearTestResult}
            onClearAction={handleClearActionResult}
            loadingText={isActionLoading ? 'Executing action...' : 'Testing connection...'}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
};

/**
 * App Dashboard Page
 *
 * Main authenticated user dashboard featuring:
 * - Retractable sidebar with Home and Sign Out navigation
 * - Header with Xacto logo and profile dropdown
 * - User info card with name, tenant, and last sign-in
 * - Plaid token card with bank connection status and reconnect button
 *
 * Requires authentication - redirects to home if not authenticated.
 *
 * @returns App dashboard page
 */
export const AppDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, session, loading: authLoading } = useAuth();



  // Redirect to login if not authenticated
  useEffect(() => {

    if (!authLoading && (!user || !session)) {

      navigate('/');
    }
  }, [authLoading, user, session, navigate]);

  // Show loading while checking auth
  if (authLoading) {

    return <LoadingState />;
  }

  // Don't render if not authenticated
  if (!user || !session) {

    return null;
  }



  // DashboardContent uses UserProvider from AppRouter context
  return <DashboardContent />;
};

export default AppDashboard;
